import { AssetType, SymbolTypes, OptionExpirationType, OptionType, } from '@tradingblock/types';
import _ from 'lodash';
import { SymbolsWithAMPMExpirations } from '../constants';
import { isString } from '../utilities';
export const AssetTypeToSymbolType = (assType) => {
    switch (assType) {
        case AssetType.Index: {
            return SymbolTypes.index;
        }
        case AssetType.Equity: {
            return SymbolTypes.stock;
        }
        default: {
            return AssetType[assType];
        }
    }
};
const isIndex = (symbol) => _.first(symbol) === '$';
export const isIndexSymbol = (symbol) => {
    return isString(symbol)
        ? isIndex(symbol)
        : symbol
            ? symbol.type === SymbolTypes.index || isIndex(symbol.symbol)
            : false;
};
export const toAssetSymbol = (symbol, assetType, symbolType, partSymb) => {
    return Object.assign({ rootSymbol: symbol, underlyingSymbol: symbol, name: symbol, provider: '' }, (partSymb || {}), { symbol, type: symbolType, assetType });
};
export const toAssetSymbolBySymbolType = (symbol, symbolType, partSymb) => {
    return toAssetSymbol(symbol, symbolType === SymbolTypes.index ? AssetType.Index : AssetType.Equity, symbolType, partSymb);
};
export const occParseRegex = /([\w]{1,4})[\s]*(\d{2})(\d{2})(\d{2})([PC])(\d*)/;
/** OCC suffix: YYMMDD + C|P + strike digits. Parsing from the right avoids greedy root capture eating the first digit of the date when the root is not space-separated (e.g. VIX241119C00025000). */
const occTailRegex = /(\d{2})(\d{2})(\d{2})([PC])(\d+)\s*$/i;
const isAmPmUnderlyingSymbol = (underlyingSymbol) => {
    const u = underlyingSymbol.trim();
    return SymbolsWithAMPMExpirations.some(s => s === u || _.replace(s, '$', '').toLowerCase() === u.replace(/^\$/, '').toLowerCase());
};
export const isOCCSymbol = (occSymbol) => {
    return parseOccSymbol(occSymbol).assetType === AssetType.Option;
};
export const deriveRootSymbol = (symbol) => {
    const parsed = parseOccSymbol(symbol);
    return parsed.assetType === AssetType.Option ? parsed.symbol : symbol;
};
export const deriveOptionExpirationType = (symbol, underlyingSymbol) => {
    if (isOCCSymbol(symbol)) {
        if (isAmPmUnderlyingSymbol(underlyingSymbol)) {
            const cleanRootSymbol = deriveRootSymbol(symbol)
                .trim()
                .toLocaleLowerCase();
            const cleanUnderlying = _.replace(underlyingSymbol, '$', '')
                .trim()
                .toLocaleLowerCase();
            return cleanRootSymbol !== cleanUnderlying ? OptionExpirationType.PM : OptionExpirationType.AM;
        }
        else {
            return OptionExpirationType.PM;
        }
    }
    return undefined;
};
export const isSymbolEqual = (one, two) => {
    if (_.isNil(one) || _.isNil(two)) {
        return false;
    }
    return (one.underlyingSymbol === two.underlyingSymbol && one.rootSymbol === two.rootSymbol && one.symbol === two.symbol);
};
export const parseOccSymbol = (occSymbol) => {
    if (!isString(occSymbol) || !occSymbol.trim()) {
        return { symbol: occSymbol || '', assetType: AssetType.Equity };
    }
    const normalized = _.trim(_.replace(occSymbol, /^\./, ''));
    const tailMatch = normalized.match(occTailRegex);
    if (tailMatch && tailMatch.index !== undefined && tailMatch.index > 0) {
        const root = _.trim(normalized.slice(0, tailMatch.index));
        if (root.length > 0) {
            const yy = tailMatch[1];
            const mm = tailMatch[2];
            const dd = tailMatch[3];
            const callput = tailMatch[4];
            const price = tailMatch[5];
            const year = 2000 + parseInt(yy, 10);
            const month = parseInt(mm, 10) - 1;
            const day = parseInt(dd, 10);
            const rootSymbol = _.trim(_.replace(root, /^\$/, ''));
            return {
                symbol: rootSymbol,
                expiration: new Date(year, month, day),
                option: callput.toLowerCase() === 'p' ? OptionType.Put : OptionType.Call,
                strike: parseInt(price, 10) / 1000,
                assetType: AssetType.Option,
            };
        }
    }
    const data = normalized.match(occParseRegex);
    if (data && data.length >= 5) {
        const symbol = data[1];
        const year = 2000 + parseInt(data[2], 10);
        const month = parseInt(data[3], 10) - 1;
        const day = parseInt(data[4], 10);
        const callput = data[5];
        const price = data[6];
        return {
            symbol,
            expiration: new Date(year, month, day),
            option: callput ? (callput.toLocaleLowerCase() === 'p' ? OptionType.Put : OptionType.Call) : undefined,
            strike: price ? parseInt(price, 10) / 1000 : undefined,
            assetType: AssetType.Option,
        };
    }
    return {
        symbol: occSymbol,
        assetType: AssetType.Equity,
    };
};
export function isOptionSymbol(val) {
    return parseOccSymbol(val).assetType === AssetType.Option;
}
export function isOptionAsset(val) {
    return val.assetType === AssetType.Option;
}
export function isMutualFundSymbol(val) {
    return val.startsWith(':');
}
//# sourceMappingURL=SymbolUtilities.js.map